import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useEffect, useState } from 'react';
import api from '../../lib/api';

const NAV_SECTIONS = [
  {
    title: 'Domain',
    items: [
      { to: '/domain/overview', icon: '◧', label: 'Domain Overview' },
      { to: '/domain/organic', icon: '◫', label: 'Organic Research' },
      { to: '/domain/traffic', icon: '⬔', label: 'Traffic Analytics' },
    ]
  },
  {
    title: 'Keyword Research',
    items: [
      { to: '/keywords/overview', icon: '⌕', label: 'Keyword Overview' },
      { to: '/keywords/magic', icon: '✦', label: 'Keyword Magic Tool' },
      { to: '/keywords/gap', icon: '⬙', label: 'Keyword Gap' },
      { to: '/keywords/difficulty', icon: '⚡', label: 'Bulk KD Checker' },
    ]
  },
  {
    title: 'Backlinks',
    items: [
      { to: '/backlinks/overview', icon: '🔗', label: 'Backlink Analytics' },
      { to: '/backlinks/gap', icon: '⧉', label: 'Backlink Gap' },
      { to: '/backlinks/bulk', icon: '⧉', label: 'Bulk Analysis' },
    ]
  },
  {
    title: 'On-Page & Tech SEO',
    items: [
      { to: '/audit/site', icon: '◍', label: 'Site Audit' },
      { to: '/audit/onpage', icon: '◎', label: 'On Page Checker' },
    ]
  },
  {
    title: 'Content',
    items: [
      { to: '/content', icon: '✎', label: 'Content Writer' },
      { to: '/checker', icon: '◐', label: 'AI Checker' },
    ]
  }
];

const PAGE_TITLES = {
  '/dashboard': 'Dashboard',
  '/domain/overview': 'Domain Overview',
  '/domain/organic': 'Organic Research',
  '/domain/traffic': 'Traffic Analytics',
  '/keywords/overview': 'Keyword Overview',
  '/keywords/magic': 'Keyword Magic Tool',
  '/keywords/gap': 'Keyword Gap',
  '/keywords/difficulty': 'Bulk Keyword Difficulty',
  '/backlinks/overview': 'Backlink Analytics',
  '/backlinks/gap': 'Backlink Gap',
  '/backlinks/bulk': 'Bulk Backlink Analysis',
  '/audit/site': 'Site Audit',
  '/audit/onpage': 'On Page SEO Checker',
  '/content': 'Content Writer',
  '/checker': 'AI Checker',
  '/pricing': 'Pricing & Credits',
  '/admin': 'Admin',
};

export default function Layout() {
  const { profile, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [globalSearch, setGlobalSearch] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    api.get('/admin/check').then(res => setIsAdmin(res.data.isAdmin)).catch(() => {});
  }, []);

  const plan = profile?.plan || 'pro';
  const credits = profile?.credits || 0;
  const max = { free: 15, starter: 500, pro: 2000, agency: 6000 }[plan] || 2000;
  const pct = Math.min(100, Math.round((credits / max) * 100));

  function handleGlobalSearch(e) {
    e.preventDefault();
    if (!globalSearch.trim()) return;
    
    const q = globalSearch.trim();
    const isDomain = q.includes('.') && !q.includes(' ');
    
    if (isDomain) {
      navigate(`/domain/overview?domain=${encodeURIComponent(q)}`);
    } else {
      navigate(`/keywords/overview?keyword=${encodeURIComponent(q)}`);
    }
  }

  return (
    <div className="layout">
      <aside className="sidebar">
        <div className="sidebar-logo">
          <div className="logo-mark">S</div>
          <span className="logo-text">SUNGRESH</span>
        </div>

        <nav className="sidebar-nav">
          <NavLink to="/dashboard" className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`} style={{ marginBottom: 16, fontWeight: 700 }}>
            <span className="nav-icon">▣</span>
            <span className="nav-label">Dashboard</span>
          </NavLink>

          {NAV_SECTIONS.map(section => (
            <div key={section.title} className="nav-section">
              <div className="nav-section-title">{section.title}</div>
              {section.items.map(item => (
                <NavLink key={item.to} to={item.to} className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
                  <span className="nav-icon">{item.icon}</span>
                  <span className="nav-label">{item.label}</span>
                </NavLink>
              ))}
            </div>
          ))}

          <div className="nav-section">
            <div className="nav-section-title">Account</div>
            <NavLink to="/pricing" className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
              <span className="nav-icon">◈</span>
              <span className="nav-label">Pricing</span>
            </NavLink>
            {isAdmin && (
              <NavLink to="/admin" className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
                <span className="nav-icon">⚙</span>
                <span className="nav-label">Admin</span>
              </NavLink>
            )}
          </div>
        </nav>

        <div className="sidebar-footer">
          <div className="credits-box">
            <div className="credits-label">Credits</div>
            <div className="credits-num">{credits.toLocaleString()}</div>
            <div className="credits-bar"><div className="credits-fill" style={{ width: `${pct}%` }} /></div>
            <div className="credits-plan">{plan.toUpperCase()} · {pct}% left</div>
          </div>
          <button className="btn btn-ghost" style={{ width: '100%', color: '#999', borderColor: '#333' }} onClick={logout}>
            Sign out
          </button>
        </div>
      </aside>

      <div className="main-area">
        <header className="topbar">
          <span className="topbar-title">{PAGE_TITLES[location.pathname] || 'Sungresh'}</span>
          
          <form onSubmit={handleGlobalSearch} className="topbar-search">
            <div style={{ display: 'flex', background: '#F3F4F6', borderRadius: 6, height: 36, overflow: 'hidden', border: '1px solid #E5E7EB' }}>
              <input 
                value={globalSearch}
                onChange={e => setGlobalSearch(e.target.value)}
                placeholder="Enter domain, keyword or URL..."
                style={{ flex: 1, border: 'none', background: 'transparent', padding: '0 14px', outline: 'none', fontSize: 13 }}
              />
              <button type="submit" style={{ background: 'var(--orange)', color: 'white', border: 'none', padding: '0 16px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                Search
              </button>
            </div>
          </form>

          <div className="topbar-right">
            <div style={{ background: '#F3F4F6', border: '1px solid #E5E7EB', borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 6, height: 6, background: '#00C853', borderRadius: '50%' }}></span>
              {credits.toLocaleString()} credits
            </div>
            <div style={{ width: 32, height: 32, background: 'var(--black)', color: 'white', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 12 }}>
              {(profile?.email || 'D')[0].toUpperCase()}
            </div>
          </div>
        </header>
        <main className="page-body"><Outlet /></main>
      </div>
    </div>
  );
}
